import { Trans, t } from "@lingui/macro";
import Image from "next/image";
import styled from "styled-components";
import { Flex, Stack } from "~/guidelines/Flex";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import { body1, heading2, heading5 } from "~/guidelines/Typography";
import accessautonome from "../../../../../new-home/_assets/promesses/accessautonome.jpg";
import annulation from "../_assets/recul_heure.svg";
import serviceclient from "../_assets/service-client.svg";
import stayCard2 from "../_assets/stay-card-2.jpeg";
import stayCard3 from "../_assets/stay-card-3.jpeg";
import wifi from "../_assets/wifi.svg";
import workspace from "../_assets/workspace.svg";

const Container = styled(Stack)`
  gap: ${({ theme }) => theme.spacing(5)};
  ${mediaQueries.md()} {
    gap: ${({ theme }) => theme.spacing(8)};
  }
`;
const SectionTitle = styled.h3`
  ${heading2}
  margin: 0;
`;
const Content = styled(Stack)`
  gap: ${({ theme }) => theme.spacing(8)};
`;
const Reassurances = styled(Stack)`
  gap: ${({ theme }) => theme.spacing(7)};
  ${mediaQueries.md()} {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(6)};
  }
`;
const Card = styled(Stack)`
  gap: ${({ theme }) => theme.spacing(5)};
  flex: 1;

  img {
    border-radius: ${({ theme }) => theme.borderRadius.default};
    width: 100%;
    object-fit: cover;
  }

  ${mediaQueries.md()} {
    gap: ${({ theme }) => theme.spacing(7)};
  }
`;
const Title = styled.h4`
  ${heading5}
  margin: 0;
  text-align: center;
`;
const Text = styled.p`
  ${body1}
  margin: 0;
  color: ${({ theme }) => theme.color.text.lightFirst};
  text-align: center;
`;
const B2B = styled(Stack)`
  gap: ${({ theme }) => theme.spacing(7)};
  padding: ${({ theme }) => theme.spacing(7)};
  background-color: ${({ theme }) => theme.color.background.darkFirst};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`;
const B2BTitle = styled.h4`
  ${heading5}
  color: ${({ theme }) => theme.color.text.darkFirst};
  text-align: center;
  margin: 0;
`;
const Pictos = styled(Stack)`
  align-items: flex-start;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing(5)};
  ${mediaQueries.md()} {
    flex-direction: row;
    align-items: center;
    gap: 0;
  }
`;
const B2BCard = styled(Flex)`
  gap: ${({ theme }) => theme.spacing(5)};
  flex-grow: 1;
  align-items: center;
  width: 100%;
  justify-content: center;

  ${mediaQueries.md()} {
    flex-direction: column;
    border-right: 1px solid ${({ theme }) => theme.color.line.darkSecond};
    &:last-child {
      border-right: none;
    }
    padding: ${({ theme }) => theme.spacing(0, 3)};
  }
`;
const B2BCardTitle = styled.h5`
  ${heading5}
  color: ${({ theme }) => theme.color.text.darkFirst};
  text-align: center;
  margin: 0;
`;
const Circle = styled.div`
  border-radius: 50%;
  background-color: ${({ theme }) => theme.color.background.lightSecond};
  width: 56px;
  height: 56px;
`;
const Circle2 = styled.div`
  display: flex;
  width: 56px;
  height: 56px;
  padding-bottom: 12px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.color.background.lightSecond};
  border-radius: 50%;
`;
const StayReassurance = () => {
  return (
    <Container>
      <SectionTitle>
        <Trans>A stay at Encore Mieux?</Trans>
      </SectionTitle>
      <Content>
        <Reassurances>
          <Card>
            <Image
              src={accessautonome.src}
              width={400}
              height={240}
              alt={t`People making his check-in online`}
            />
            <Title>
              <Trans>
                A hotel without a front desk, and a fully autonomous arrival
              </Trans>
            </Title>
            <Text>
              <Trans>
                No reception, no waiting in line, your smartphone does it all.
                <br />
                It becomes your key to the hotel and your room, thanks to a
                secure link sent by SMS and email on the day of your arrival.
                <br />
                You&apos;re free to enter, on your own schedule.
              </Trans>
            </Text>
          </Card>
          <Card>
            <Image
              src={stayCard2.src}
              width={400}
              height={240}
              alt={t`A nice breakfast in the hotel`}
            />
            <Title>
              <Trans>A complete and carefully prepared breakfast</Trans>
            </Title>
            <Text>
              <Trans>
                No generic buffet here. At Encore Mieux, breakfast highlights
                fresh, local products: bread, brioche, pastries from the
                neighborhood bakery, healthy options, seasonal fruits, jams and
                juices. All available in a calm, pleasant setting to start your
                day right.
              </Trans>
            </Text>
          </Card>
          <Card>
            <Image
              src={stayCard3.src}
              width={400}
              height={240}
              alt={t`A separation between the bathroom and the bedroom`}
            />
            <Title>
              <Trans>
                Hotels designed to be simple, comfortable, and lasting
              </Trans>
            </Title>
            <Text>
              <Trans>
                Soundproof rooms, high-quality bedding, quiet air conditioning,
                blackout curtains, and reusable materials.
                <br />
                No excess, just the essentials so you can rest or work in peace.
              </Trans>
            </Text>
          </Card>
        </Reassurances>
        <B2B>
          <B2BTitle>
            <Trans>{`We don't forget the business travellers!`}</Trans>
          </B2BTitle>
          <Pictos>
            <B2BCard>
              <Circle>
                <Image src={wifi.src} alt="" width={51} height={45} />
              </Circle>
              <B2BCardTitle>
                <Trans>Simplified Wi-Fi access</Trans>
              </B2BCardTitle>
            </B2BCard>
            <B2BCard>
              <Circle2>
                <Image src={serviceclient.src} alt="" width={65} height={52} />
              </Circle2>
              <B2BCardTitle>
                <Trans>24/7 online concierge</Trans>
              </B2BCardTitle>
            </B2BCard>
            <B2BCard>
              <Image src={workspace.src} alt="" width={57} height={56} />
              <B2BCardTitle>
                <Trans>Quiet workspace</Trans>
              </B2BCardTitle>
            </B2BCard>
            <B2BCard>
              <Image src={annulation.src} alt="" width={67} height={52} />
              <B2BCardTitle>
                <Trans>Flexible cancellation</Trans>
              </B2BCardTitle>
            </B2BCard>
          </Pictos>
        </B2B>
      </Content>
    </Container>
  );
};

export default StayReassurance;
